import { Divider, Icon, TileText } from '@design-system';
import { cn } from '@utils/cn';
import { AnimatePresence, domAnimation, LazyMotion, m } from 'framer-motion';
import { useTranslation } from 'next-i18next';

import { Image } from '../../../../components/Image';

export type DrawSummaryCardProps = {
  className?: string;
  frameOption?: string;
  framePrice?: string;
  hasFrameOptions?: boolean;
  image: string;
  isOpen: boolean;
  isUnframed?: boolean;
  productPrice: string;
  subText?: string;
  subTextState?: string;
  title: string;
  totalPrice: string;
  onAddFrame?: () => void;
  onToggle: () => void;
};

export const DrawSummaryCard = ({
  className,
  frameOption,
  framePrice,
  hasFrameOptions = true,
  image,
  isOpen,
  isUnframed,
  onAddFrame,
  onToggle,
  productPrice,
  subText,
  subTextState,
  title,
  totalPrice,
}: DrawSummaryCardProps) => {
  const { t } = useTranslation(['product', 'checkout']);

  const resolveFrameLabel = () => {
    if (frameOption) return frameOption;
    if (isUnframed) return t('checkout:framing.unframed');

    return t('framing.notSelected');
  };

  // Without frame options the expanded content would only repeat the title
  // and price, so the card is a static row with no chevron.
  const summary = (
    <>
      <div className="flex min-w-0 flex-1 flex-row items-center gap-3">
        {image && (
          <div className="h-10 w-10 flex-shrink-0">
            <Image
              className="h-full w-full rounded-sm"
              src={image}
              alt={title}
            />
          </div>
        )}
        <div className="flex min-w-0 flex-col">
          <TileText className="line-clamp-1 w-full">{title}</TileText>
          {subText && (
            <TileText
              className={cn('line-clamp-1 w-full text-xs', subTextState)}
            >
              {subText}
            </TileText>
          )}
        </div>
      </div>
      <div className="flex flex-shrink-0 flex-row items-center gap-2">
        <TileText className="text-s lg:text-sm">{totalPrice}</TileText>
        {hasFrameOptions && (
          <span className="shrink-0">
            <Icon
              name={isOpen ? 'ic_chevron_up' : 'ic_chevron_down'}
              size="sm"
            />
          </span>
        )}
      </div>
    </>
  );

  return (
    <div
      className={cn(
        'm-4 flex flex-col rounded-sm border border-divider/20 transition-colors',
        className,
        {
          'hover:border-divider/40': hasFrameOptions && !isOpen,
        },
      )}
    >
      {hasFrameOptions ? (
        <button
          className="flex flex-row justify-between gap-3 p-4"
          onClick={onToggle}
          type="button"
        >
          {summary}
        </button>
      ) : (
        <div className="flex flex-row justify-between gap-3 p-4">{summary}</div>
      )}
      <LazyMotion features={domAnimation}>
        <AnimatePresence initial={false}>
          {hasFrameOptions && isOpen && (
            <m.section
              key="content"
              animate="open"
              className="overflow-hidden"
              exit="collapsed"
              initial="collapsed"
              transition={{ duration: 0.3 }}
              variants={{
                open: { opacity: 1, height: 'auto' },
                collapsed: { opacity: 0, height: 0 },
              }}
            >
              <div className="flex flex-col gap-4 px-4 pb-4">
                <Divider />
                <div className="flex w-full flex-col gap-2">
                  <div className="flex flex-row justify-between gap-2">
                    <TileText className="line-clamp-1 w-auto">{title}</TileText>
                    <TileText className="w-auto">{productPrice}</TileText>
                  </div>
                  <div className="flex flex-row justify-between gap-2">
                    {!frameOption && onAddFrame ? (
                      <button
                        className="text-left text-neutral-6 underline transition-colors hover:text-neutral-4"
                        onClick={onAddFrame}
                        type="button"
                      >
                        {t('framing.addFrame')}
                      </button>
                    ) : (
                      <TileText
                        className={cn(
                          'w-auto',
                          frameOption || isUnframed
                            ? 'text-neutral-6'
                            : 'text-neutral-4',
                        )}
                      >
                        {resolveFrameLabel()}
                      </TileText>
                    )}
                    <TileText className="w-auto">{framePrice ?? '-'}</TileText>
                  </div>
                </div>
              </div>
            </m.section>
          )}
        </AnimatePresence>
      </LazyMotion>
    </div>
  );
};
